<script setup lang="ts">
import ImagePlusIcon from "@/assets/icons/image-plus.svg";
import {
  IMediaCenterSelection,
  MediaCenterAllowedExtensions,
  MediaCenterSubCategory,
} from "@/helpers/mediaCenter.type";
import { triggerMediaCenterOpen } from "@/helpers/mediaCenterUtil";
import { useAppStore } from "@/store/app";
import { useI18n } from "vue-i18n";

const store = useAppStore();
const { t } = useI18n();

const props = defineProps({
  allowedExtensions: {
    type: Array as () => MediaCenterAllowedExtensions[],
    default: () => [MediaCenterAllowedExtensions.IMAGE],
  },
  subCategory: {
    type: String as () => MediaCenterSubCategory,
    default: MediaCenterSubCategory.FORM,
  },
  maxUploadSize: {
    type: Object as () => {
      video?: number;
      audio?: number;
      document?: number;
      image?: number;
    },
    default: () => ({}),
  },
  allowMultipleFiles: {
    type: Boolean,
    default: false,
  },
  value: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: "Image URL",
  },
  label: {
    type: String,
    default: "Image",
  },
});

const emit = defineEmits(["update:value"]);

const handleMediaCenterSelection = (data: IMediaCenterSelection) => {
  emit("update:value", data.url);
  // Media picker selection should behave like any editable change in builder
  store.anyUnsavedChanges = true;
  store.addToHistory();
};

const openMediaCenter = () => {
  triggerMediaCenterOpen({
    appHandshake: store.appHandshake,
    settings: {
      allowedExtensions: props.allowedExtensions,
      maxUploadSize: props.maxUploadSize,
      allowMultipleFileInsertion: props.allowMultipleFiles,
      subCategory: props.subCategory,
    },
    onSelect: handleMediaCenterSelection,
  });
};

const handleInputChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (props.value === undefined || props.value !== target.value) {
    emit("update:value", target.value);
    store.anyUnsavedChanges = true;
  }
};
</script>

<template>
  <div class="form-group">
    <div class="flex space-x-3">
      <span class="hl-text-input-label mb-1 block text-sm font-medium text-gray-700">{{
        label
      }}</span>
    </div>

    <div class="relative mt-1 flex h-10 w-full items-center overflow-hidden rounded-md shadow-sm">
      <input
        id="media-center-input"
        type="text"
        class="block h-full w-full rounded-l-md border border-gray-300 pl-3 sm:text-sm"
        :placeholder="placeholder"
        :value="value"
        @input="handleInputChange"
      />

      <button
        id="media-center-open-button"
        class="relative inline-flex h-full items-center rounded-r-md border border-gray-300 !bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
        @click="openMediaCenter"
      >
        <ImagePlusIcon />
      </button>
    </div>
  </div>
</template>

<style scoped>
#media-center-open-button {
  border-left: 0px !important;
}
</style>
